import { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import { FilterContext, FilterDispatchContext } from "../Context/FilterContext";
import { SearchIcon } from "../Util/SVGIcons";
import LocationCombobox from "./Filters/LocationCombobox";
import PriceInputs from "./Filters/PriceInputs";
import PropertyTypeDropdown from "./Filters/PropertyTypeDropdown";
import MoreFiltersPanel from "./Filters/MoreFiltersPanel";

const Bar = styled.div`
  .filter-bar {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;

    @media screen and (min-width: 768px) {
      flex-wrap: nowrap;
    }
  }

  .transaction-toggle {
    display: flex;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: var(--elevation-1dp);
    flex-shrink: 0;

    button {
      padding: 10px 18px;
      border: none;
      background: #fff;
      font-size: 14px;
      font-weight: 600;
      color: var(--primary-grey-300);
      cursor: pointer;
      transition: all 150ms;

      &.active {
        background: var(--color-secondary-500);
        color: #fff;
      }

      &:not(.active):hover {
        background: var(--background-grey-100);
      }
    }
  }

  .keyword-input {
    flex: 1;
    min-width: 150px;
    position: relative;

    @media screen and (max-width: 767px) {
      order: -1;
      width: 100%;
      flex-basis: 100%;
    }

    form {
      display: flex;
      align-items: stretch;
      background: #fff;
      border-radius: 8px;
      box-shadow: var(--elevation-1dp);
      overflow: hidden;
      transition: all 150ms;

      &:focus-within {
        box-shadow: var(--elevation-2dp);
      }
    }

    input {
      flex: 1;
      padding: 10px 14px;
      border: none;
      font-size: 14px;
      outline: none;
      min-width: 0;

      &::placeholder {
        color: var(--color-secondary-200);
      }
    }

    button {
      padding: 0 14px;
      background: var(--color-primary);
      border: none;
      border-radius: 0 8px 8px 0;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;

      &:hover {
        opacity: 0.9;
      }
    }
  }

  .more-filters-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 10px 14px;
    border: none;
    border-radius: 8px;
    background: #fff;
    font-size: 14px;
    color: var(--primary-grey-500);
    cursor: pointer;
    box-shadow: var(--elevation-1dp);
    transition: all 150ms;
    white-space: nowrap;
    flex-shrink: 0;

    &:hover {
      box-shadow: var(--elevation-2dp);
    }

    &.has-filters {
      background: var(--primary-100);
    }

    .badge-count {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: var(--color-primary);
      color: #fff;
      font-size: 11px;
      font-weight: 700;
    }
  }
`;

const MORE_FILTER_KEYS = ["bedrooms", "bathrooms", "parking", "floorAreaMin", "floorAreaMax", "lotAreaMin", "lotAreaMax", "furnishing"];

const InlineFilterBar = () => {
  const filters = useContext(FilterContext) || {};
  const dispatch = useContext(FilterDispatchContext);
  const location = useLocation();

  const [keyword, setKeyword] = useState(filters.searchKeyword || "");
  const [moreOpen, setMoreOpen] = useState(false);

  // Sync URL params to filter state on mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.has("q")) {
      const mapping = {
        transactionType: "transactionType",
        propertyType: "propertyType",
        location: "location",
        keyword: "searchKeyword",
        collection: "collection",
      };
      Object.entries(mapping).forEach(([urlKey, filterKey]) => {
        const val = params.get(urlKey);
        if (val) {
          dispatch({ action: "set", filter: { key: filterKey, value: val } });
        }
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep keyword input in sync with filter context
  useEffect(() => {
    setKeyword(filters.searchKeyword || "");
  }, [filters.searchKeyword]);

  const handleTransactionType = (type) => {
    if (filters.transactionType === type) {
      dispatch({ action: "delete", filter: { key: "transactionType" } });
    } else {
      dispatch({ action: "set", filter: { key: "transactionType", value: type } });
    }
  };

  const handleKeywordSubmit = (e) => {
    e.preventDefault();
    if (keyword.trim()) {
      dispatch({ action: "set", filter: { key: "searchKeyword", value: keyword.trim() } });
    } else {
      dispatch({ action: "delete", filter: { key: "searchKeyword" } });
    }
  };

  const moreFilterCount = MORE_FILTER_KEYS.filter((k) => filters[k]).length;

  return (
    <Bar>
      <div className="filter-bar">
        <div className="transaction-toggle">
          <button
            type="button"
            className={filters.transactionType === "sale" ? "active" : ""}
            onClick={() => handleTransactionType("sale")}
          >
            Buy
          </button>
          <button
            type="button"
            className={filters.transactionType === "lease" ? "active" : ""}
            onClick={() => handleTransactionType("lease")}
          >
            Lease
          </button>
        </div>

        <div className="keyword-input">
          <form onSubmit={handleKeywordSubmit}>
            <input
              type="text"
              placeholder="Keyword search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
            />
            <button type="submit">
              <SearchIcon fill="#fff" height={18} width={18} />
            </button>
          </form>
        </div>

        <LocationCombobox />
        <PropertyTypeDropdown />
        <PriceInputs />

        <button
          type="button"
          className={`more-filters-btn${moreFilterCount > 0 ? " has-filters" : ""}`}
          onClick={() => setMoreOpen(!moreOpen)}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="4" y1="6" x2="20" y2="6"/>
            <line x1="4" y1="12" x2="20" y2="12"/>
            <line x1="4" y1="18" x2="20" y2="18"/>
            <circle cx="8" cy="6" r="2" fill="currentColor"/>
            <circle cx="16" cy="12" r="2" fill="currentColor"/>
            <circle cx="10" cy="18" r="2" fill="currentColor"/>
          </svg>
          More Filters
          {moreFilterCount > 0 && (
            <span className="badge-count">{moreFilterCount}</span>
          )}
        </button>
      </div>

      <MoreFiltersPanel isOpen={moreOpen} onClose={() => setMoreOpen(false)} />
    </Bar>
  );
};

export default InlineFilterBar;
